/**
 * VirtualAccountManagement Component
 * 가상계좌 관리 페이지
 */
import React, { useState, useEffect } from 'react';
import { useVirtualAccountData } from '../hooks/useVirtualAccountData';
import { VirtualAccountFilters, VirtualAccount } from '../types/virtualAccount.types';
import { virtualAccountApi } from '../api/virtualAccountApi';
import AccountTable from './AccountTable';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import '@/shared/styles/search-form.css';
import '../styles/VirtualAccountManagement.css';

const VirtualAccountManagement: React.FC = () => {
    const { accounts, loading, totalElements, totalPages, fetchAccounts } = useVirtualAccountData();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(30);
    const [filters, setFilters] = useState<VirtualAccountFilters>(() => {
        return loadFilters<VirtualAccountFilters>('virtualAccountFilters') || {};
    });
    const [suppId, setSuppId] = useState('');
    const [riderId, setRiderId] = useState('');
    const [searchResults, setSearchResults] = useState<VirtualAccount[]>([]);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    useEffect(() => {
        fetchAccounts(filters, 0, 9999);
    }, [filters, fetchAccounts]);

    const handleFilterChange = (newFilters: VirtualAccountFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
        saveFilters('virtualAccountFilters', newFilters);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleSuppIdSearch = async () => {
        if (!suppId.trim()) {
            alert('협력사 ID를 입력해주세요.');
            return;
        }

        setSearchLoading(true);
        try {
            const result = await virtualAccountApi.getVirtualAccountSuppId(suppId.trim());
            setSearchResults(Array.isArray(result) ? result : [result]);
            setIsSearchMode(true);
            setCurrentPage(1);
        } catch (error: any) {
            const errorMsg = error?.originalError?.response?.data?.message
                || error?.message
                || '검색에 실패했습니다.';
            alert(errorMsg);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleRiderIdSearch = async () => {
        if (!riderId.trim()) {
            alert('라이더 ID를 입력해주세요.');
            return;
        }

        setSearchLoading(true);
        try {
            const result = await virtualAccountApi.getVirtualAccountriderId(riderId.trim());
            setSearchResults(Array.isArray(result) ? result : [result]);
            setIsSearchMode(true);
            setCurrentPage(1);
        } catch (error: any) {
            const errorMsg = error?.originalError?.response?.data?.message
                || error?.message
                || '검색에 실패했습니다.';
            alert(errorMsg);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleResetSearch = () => {
        setIsSearchMode(false);
        setSearchResults([]);
        setSuppId('');
        setRiderId('');
        setCurrentPage(1);
    };

    const displayAccounts = isSearchMode ? searchResults : accounts;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentAccounts = displayAccounts.slice(startIndex, endIndex);
    const calculatedTotalPages = Math.ceil(displayAccounts.length / pageSize);

    if (loading) {
        return <Loading message="가상계좌 데이터를 불러오는 중..." />;
    }

    return (
        <div className="virtual-account-management">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">가상계좌 관리</h1>
            </div>

            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form virtual-account-search-form">
                        {/* 협력사 ID 검색 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사 ID
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    value={suppId}
                                    onChange={(e) => setSuppId(e.target.value)}
                                    placeholder="협력사 ID 입력..."
                                    onKeyPress={(e) => e.key === 'Enter' && handleSuppIdSearch()}
                                />
                            </div>
                        </div>

                        {/* 라이더 ID 검색 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-motorcycle" />
                                    라이더 ID
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    value={riderId}
                                    onChange={(e) => setRiderId(e.target.value)}
                                    placeholder="라이더 ID 입력..."
                                    onKeyPress={(e) => e.key === 'Enter' && handleRiderIdSearch()}
                                />
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            {isSearchMode && (
                                <button onClick={handleResetSearch} className="btn btn-secondary">
                                    <i className="fas fa-redo" />
                                    초기화
                                </button>
                            )}
                            <button
                                onClick={handleSuppIdSearch}
                                disabled={searchLoading || !suppId.trim()}
                                className="btn btn-primary"
                            >
                                <i className="fas fa-building" />
                                협력사 조회
                            </button>
                            <button
                                onClick={handleRiderIdSearch}
                                disabled={searchLoading || !riderId.trim()}
                                className="btn btn-primary"
                            >
                                <i className="fas fa-motorcycle" />
                                라이더 조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <div className="section-header">
                    <h2>{isSearchMode ? '검색 결과' : '가상계좌 목록'}</h2>
                    <span className="total-count">총 {displayAccounts.length}건</span>
                </div>

                <AccountTable accounts={currentAccounts} loading={false} />

                {calculatedTotalPages > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={calculatedTotalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default VirtualAccountManagement;
